import "../styles/Buttons.css";
import { makeStyles, Box, TableCell } from "@material-ui/core";
import { useEffect, useState } from "react";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { DatePickerComponent, DatePickerWrapper, MAX_DATE, MIN_DATE } from "../components/AppToolbar";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import OverStaffReport from "../models/OverStaffReport/OverStaffReport";
import UserService from "../services/UserService";
import { RequestStatus } from "../types/RequestStatus";
import OpsDateUtils from "../utils/OpsDateUtils";
import ReportTable from "../components/ReportTable";
import { ReportHeader } from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import { useMaxStoredDate } from "../hooks/useStoredDate";
import { SelectComponent } from "../components/SelectComponent";
import { OverStaffAssignationDetails } from "../components/OverStaffReport/OverStaffAssignationDetails";
import { RoleOptions } from "../utils/enums/EnsolversOpsRoles";
import { StaffReportTabs } from "../utils/enums/StaffReportTabs";
import { StaffTabsToolbar } from "../components/OverStaffReport/StaffTabsToolbar";
import { AvailableUsersTab } from "../components/OverStaffReport/AvailableUsersTab";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import useWindowSize from "../hooks/useWindowSize";
import { CSVLink } from "react-csv";
import DownloadIcon from "@mui/icons-material/Download";
import { Button } from "@mui/material";
import { useLocation } from "react-router-dom";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { Routes } from "../types/Routes";
import useTableHeight from "../hooks/useTableHeight";
import { ColorUtils } from "../utils/ColorUtils";

export default function OverStaffReportList() {
  enum filterOptions {
    ALL = "All",
    GREATER_THAN_8 = "> 8",
    EQUAL_TO_8 = "= 8",
    LESS_THAN_8 = "< 8",
    LESS_THAN_6 = "< 6",
    LESS_THAN_4 = "< 4",
  }

  const classes = useStyles();
  const { isMobileWidth } = useWindowSize();
  const [isOpen, setIsOpen] = useState(false);
  const [requestStatus, setRequestStatus] = useState<RequestStatus | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useMaxStoredDate();
  const [headers, setHeaders] = useState<ReportHeader[] | undefined>(undefined);
  const [rows, setRows] = useState<OverStaffReport[]>([]);
  const [filter, setFilter] = useState<string>("All");
  const [userName, setUserName] = useState<string>("");
  const [userExternalId, setUserExternalId] = useState<string | undefined>(undefined);
  const [roleFilter, setRoleFilter] = useState<string>("All");
  const [selectedTab, setSelectedTab] = useState<number>(StaffReportTabs.StaffReport);
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const searchParams = new URLSearchParams(useLocation().search);
  const report = searchParams.get("report");
  const month = searchParams.get("month");
  const year = searchParams.get("year");
  const hours = searchParams.get("hours");
  const role = searchParams.get("role");

  const {updateUrl} = useUpdateUrl(Routes.OVER_STAFF_REPORT, searchParams);

  useEffect(() => {
    const date = new Date();

    if (month !== null) {
      date.setMonth(parseInt(month));
      setSelectedDate(date);
    } else {
      searchParams.set("month", (date.getMonth() - 1).toString());
    }

    if (year !== null) {
      date.setFullYear(parseInt(year));
      setSelectedDate(date);
    } else {
      searchParams.set("year", date.getFullYear().toString());
    }

    if (hours !== null) {
      setFilter(hours);
    } else {
      searchParams.set("hours", "All");
    }

    if (role !== null) {
      setRoleFilter(role);
    } else {
      searchParams.set("role", "All");
    }

    if (report !== null) {
      setSelectedTab(parseInt(report));
    } else {
      searchParams.set("report", selectedTab.toString());
    }

    if (selectedTab && selectedTab === StaffReportTabs.AvailableStaffReport) {
      searchParams.delete("hours");
      searchParams.delete("role");
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab]);

  useEffect(() => {
    loadHeaders();
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, roleFilter]);

  const loadHeaders = () => {
    let headers = new Array<ReportHeader>();
    headers.push({ label: "Team members", align: "left" });
    headers.push({ label: "Prim Role", align: "left" });
    headers.push({ label: "Sec Role", align: "left" });
    headers.push({ label: "Current Project", align: "left" });
    OpsDateUtils.getWeeksOfMonth(selectedDate.getFullYear(), selectedDate.getMonth()).forEach((d) =>
      headers.push({ label: `${d.startDate} - ${d.endDate}`, align: "center" })
    );
    setHeaders(headers);
  };

  const loadContent = async () => {
    setRequestStatus(RequestStatus.LOADING);

    (await UserService.getOverStaffReport(selectedDate.getFullYear(), selectedDate.getMonth(), filter, roleFilter))
      .onSuccess((response) => {
        setRows(response);
        setRequestStatus(RequestStatus.SUCCESS);
      })
      .onError((response) => {
        setRequestStatus(RequestStatus.ERROR);
        NotificationService.error(response);
      });
  };

  const getSumOfExternalHours = (hours: Map<number, number>): number => {
    let sum: number = 0;
    Object.entries(hours).forEach((map) => (sum += map[1]));
    return sum;
  };

  const changeTab = async (_event: React.SyntheticEvent, tab: number) => {
    setSelectedTab(tab);
    searchParams.set("report", tab.toString());
    updateUrl();
  };

  const getStringForCsv = (report: OverStaffReport[]) => {
    let result: string = "";
    report.forEach((r: OverStaffReport) => {
      let workerData: string = `${r.teamMember},${r.primaryRole === undefined ? "-" : r.primaryRole},${
        r.secondaryRole === undefined ? "-" : r.secondaryRole
      },${r.projectNames}`;

      const hoursArray: number[] = Object.entries(r.hours).map(([week, value], index) => {
        return value;
      });
      hoursArray.forEach((hours) => {
        workerData = workerData + `,${hours}`;
      });

      result = result + workerData + "\n";
    });
    return result;
  };

  const renderDownloadSection = (
    <Box display={"flex"} style={{ paddingLeft: ".5em" }}>
      <Button startIcon={<DownloadIcon />}>
        <CSVLink
          data={getStringForCsv(rows)}
          headers={headers?.map((e) => e.label)}
          asyncOnClick
          filename={`OverStaff Report - ${selectedDate.toDateString()}`}
          className="download-link"
        >
          Download as CSV
        </CSVLink>
      </Button>
    </Box>
  );

  const rederDatePicker = (
    <DatePickerWrapper>
      <DatePickerComponent
        views="month"
        label="Month"
        value={selectedDate}
        inputFormat="MMMM"
        onChange={(selectedDate) => {
          setSelectedDate(selectedDate!);
          searchParams.set("month", selectedDate!.getMonth().toString());
          updateUrl();
        }}
      />
      <DatePickerComponent
        views="year"
        label="Year"
        value={selectedDate}
        inputFormat="yyyy"
        minDate={MIN_DATE}
        maxDate={MAX_DATE}
        onChange={(selectedDate) => {
          setSelectedDate(selectedDate!);
          searchParams.set("year", selectedDate!.getFullYear().toString());
          updateUrl();
        }}
      />
    </DatePickerWrapper>
  );

  const renderSelectExternalHoursAndRole = (
    <>
      <SelectComponent
        label={"External hours"}
        value={filter}
        setValue={(value) => {
          setFilter(value);
          searchParams.set("hours", value);
          updateUrl();
        }}
        options={Object.values(filterOptions)}
        size="small"
      />
      <SelectComponent
        label={"Role"}
        value={roleFilter}
        setValue={(value) => {
          setRoleFilter(value);
          searchParams.set("role", value);
          updateUrl();
        }}
        options={Object.values(RoleOptions)}
        size="small"
      />
    </>
  );

  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        <div>{rederDatePicker}</div>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
          {selectedTab === 0 && <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />}
          {renderDownloadSection}
        </div>
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {rederDatePicker}
        {selectedTab === 0 && renderSelectExternalHoursAndRole}
        {renderDownloadSection}
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const getHeader = () => {
    return (
      <>
        <StaffTabsToolbar selectedTab={selectedTab} handleTabChange={changeTab}>
          {renderFilters()}
        </StaffTabsToolbar>
      </>
    );
  };

  const getBody = () => {
    return (
      <>
        <ReportTable<OverStaffReport>
          maxHeight={calculateMaxHeight(false)}
          stickyHeader
          headers={headers!}
          rows={rows}
          denseTable
          onRowClick={(row) => {
            setUserName(row.teamMember);
            setUserExternalId(row.teamMemberExternalId);
          }}
          tableRow={(instance) => (
            <>
              <TableCell>{instance.teamMember}</TableCell>
              <TableCell>{instance.primaryRole ? instance.primaryRole : "-"}</TableCell>
              <TableCell>{instance.secondaryRole ? instance.secondaryRole : "-"}</TableCell>
              <TableCell>{instance.projectNames ? instance.projectNames : "-"}</TableCell>
              {Object.entries(instance.hours).map(([week, value], index) => (
                <TableCell align="center">{value}</TableCell>
              ))}
            </>
          )}
        />

        <OverStaffAssignationDetails userName={userName} userExternalId={userExternalId!} setUserExternalId={setUserExternalId} />

        {/* Dialog to manage filters  */}
        <FilterDialog title={"Staff"} setIsOpen={setIsOpen} isOpen={isOpen}>
          {renderSelectExternalHoursAndRole}
        </FilterDialog>
      </>
    );
  };

  const getAvailableStaff = () => {
    return (
      <>
        <AvailableUsersTab calculateMaxHeight={calculateMaxHeight} selectedDate={selectedDate} />
      </>
    );
  };

  return requestStatus === RequestStatus.ERROR ? (
    <></>
  ) : (
    <>
      <AppContainerFullHeight
        bottomSectionRef={bottomSectionRef}
        topSection={getHeader()}
        bottomSection={selectedTab === 0 ? getBody() : getAvailableStaff()}
      />
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  desktopFilters: {
    borderRadius: "4px",
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${ColorUtils.LIGHTER_GRAY}`,
    boxSizing: 'inherit',
    boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "row",
    "&> div:first-child": {
      "&> div": {
        width: "100% !important",
        maxWidth: "200px !important",
        minWidth: "0 !important",
      },
    },
  },
  mobileFilters: {
    width: "100% !important",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    padding: "5px",
    "&> div:first-child": {
      display: "flex",
      flexDirection: "row",
      columnGap: "10px",
      "&> div > div": {
        width: "50% !important",
        margin: "0 !important",
      },
    },
  },
  [theme.breakpoints.down("sm")]: {
    desktopFilters: {
      width: "100% !important",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridRowGap: "10px",
      gridColumnGap: "10px",
      "&> div": {
        width: "100%",
        margin: "0 !important",
      },
      "&> div:nth-child(1)": {
        "&> div": {
          width: "100% !important",
          maxWidth: "none !important",
        },
      },
    },
  },
}),
{ index: 1 }
);
